import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu as MenuIcon } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/menu", label: "Menu" },
    { to: "/order", label: "Order" },
    { to: "/events", label: "Events" },
    { to: "/gallery", label: "Gallery" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Book" },
];

export default function SiteNav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header
            data-testid="site-nav"
            className={`sticky top-0 z-40 transition-shadow ${
                scrolled ? "shadow-[0_8px_32px_rgba(0,0,0,0.55)]" : ""
            }`}
            style={{
                backgroundColor: scrolled ? "rgba(8,8,9,0.86)" : "rgba(8,8,9,0.55)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                borderBottom: scrolled ? "1px solid rgba(255,140,0,0.18)" : "1px solid rgba(255,255,255,0.06)",
            }}
        >
            <div className="container-narrow flex items-center justify-between py-3 md:py-4">
                <Link to="/" className="flex items-center gap-3 logo-glow" data-testid="nav-logo-link">
                    <BrandLogo size={42} />
                    <div className="leading-none hidden sm:block">
                        <div className="font-display text-xl tracking-[0.06em] text-white">SMOKIN&rsquo; MO&rsquo;S</div>
                        <div className="text-[10px] uppercase tracking-[0.32em] text-orange-300/80">Southern BBQ</div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-1" data-testid="nav-desktop">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-3 py-2 rounded-md text-sm tracking-wide uppercase font-medium transition-colors ${
                                    isActive ? "text-orange-300" : "text-white/80 hover:text-white"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="nav-book-catering-button"
                        className="ember-btn ml-3 px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase"
                    >
                        Book Catering
                    </Link>
                </nav>

                <div className="md:hidden">
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                aria-label="Open menu"
                                data-testid="nav-mobile-menu-button"
                                className="p-2 rounded-md border border-white/10 bg-black/40"
                            >
                                <MenuIcon size={20} />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-[#0a0a0b] border-white/10 text-white" data-testid="nav-mobile-sheet">
                            <div className="flex items-center gap-3 mb-6">
                                <BrandLogo size={36} />
                                <span className="font-display tracking-[0.06em] text-lg">SMOKIN&rsquo; MO&rsquo;S</span>
                            </div>
                            <div className="flex flex-col gap-1">
                                {LINKS.map((l) => (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        end={l.to === "/"}
                                        data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                        className={({ isActive }) =>
                                            `px-3 py-3 rounded-md text-base font-display tracking-[0.06em] uppercase ${
                                                isActive ? "bg-white/5 text-orange-300" : "text-white/85"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                ))}
                                <Link
                                    to="/contact"
                                    data-testid="nav-mobile-book-catering-button"
                                    className="ember-btn mt-3 px-4 py-3 rounded-md text-center font-display tracking-[0.12em] uppercase"
                                >
                                    Book Catering
                                </Link>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
